import React, { FC, useState, useRef, useEffect } from 'react';

import { CardSchema } from 'constants/graphqlTypes';
import LoaderContent from 'components/UI/LoaderContent';
import SparkCard from 'components/Sparks/Views/SparkCard';

import sparkIconLight from 'assets/videos/spark-icon-light.webm';
import sparkIconDark from 'assets/videos/spark-icon-dark.webm';

import browseSparksImage from 'assets/images/browse-sparks-image.png';
import SparkSelectionModal from './SparkSelectionModal';

import {
  StyledSparksListContainer,
  StyledEmptyState,
  StyledEmptyText,
  StyledBrowseSparksButton,
  StyledButtonText,
  StyledSparkIconVideo,
  StyledSparkCardWrapper,
  StyledOverlay,
  StyledIllustrationImage,
} from './styled';

interface ISparksListProps {
  sparks: CardSchema[];
  loading: boolean;
  isMyProfile: boolean;
  fontColor?: string;
  isDark?: boolean;
  gradientStartColor?: string | null;
  isTabReady?: boolean; // Whether the Sparks tab slide transition is complete
}

const SparksList: FC<ISparksListProps> = ({
  sparks,
  loading,
  isMyProfile,
  fontColor,
  isDark,
  gradientStartColor,
  isTabReady = true,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [overlayHeight, setOverlayHeight] = useState(0);
  const firstCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Calculate overlay height after initial render and when sparks change
    const calculateHeight = () => {
      if (firstCardRef.current) {
        setOverlayHeight(firstCardRef.current.getBoundingClientRect().height);
      }
    };

    // Run immediately and again after a brief delay to ensure the DOM is ready
    calculateHeight();
    const timeoutId = setTimeout(calculateHeight, 100);

    return () => clearTimeout(timeoutId);
  }, [sparks]);

  const handleCreateSpark = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  if (loading) {
    return (
      <StyledSparksListContainer>
        <LoaderContent isCentered isDark={isDark} />
      </StyledSparksListContainer>
    );
  }

  // Empty state
  if (!sparks.length) {
    return (
      <StyledSparksListContainer>
        {/* Show gradient overlay for empty state like other tabs */}
        {!!gradientStartColor && (
          <StyledOverlay
            isDark={isDark ?? false}
            startColor={gradientStartColor}
            isMyProfile={isMyProfile}
            height={300}
          />
        )}
        {isMyProfile && <StyledIllustrationImage src={browseSparksImage} alt="Browse sparks illustration" />}
        <StyledEmptyState style={!isMyProfile ? { paddingTop: '80px' } : undefined}>
          <StyledEmptyText fontColor={fontColor} isDark={isDark}>
            {isMyProfile ? "You haven't created a spark yet" : "This user hasn't added any Sparks yet"}
          </StyledEmptyText>
          {isMyProfile && (
            <StyledBrowseSparksButton isDark={isDark} onClick={handleCreateSpark}>
              <StyledSparkIconVideo src={isDark ? sparkIconDark : sparkIconLight} autoPlay loop muted playsInline />
              <StyledButtonText isDark={isDark}>Browse sparks</StyledButtonText>
            </StyledBrowseSparksButton>
          )}
        </StyledEmptyState>
        <SparkSelectionModal isOpen={isModalOpen} onClose={handleCloseModal} showIntro />
      </StyledSparksListContainer>
    );
  }

  return (
    <StyledSparksListContainer>
      {!!gradientStartColor && overlayHeight > 0 && (
        <StyledOverlay
          isDark={isDark ?? false}
          startColor={gradientStartColor}
          isMyProfile={isMyProfile}
          height={overlayHeight}
        />
      )}
      {sparks.map((spark, index) => (
        <StyledSparkCardWrapper key={spark.id} ref={index === 0 ? firstCardRef : undefined}>
          <SparkCard
            isDark={isDark}
            fontColor={index === 0 ? fontColor : undefined}
            card={spark}
            minimal
            isOtherUser={!isMyProfile}
            isFirstItem={index === 0}
            isTabReady={isTabReady}
            showFooterMenu
          />
        </StyledSparkCardWrapper>
      ))}
      <SparkSelectionModal isOpen={isModalOpen} onClose={handleCloseModal} />
    </StyledSparksListContainer>
  );
};

export default SparksList;
